// Hotel Admin Service
import { 
  HotelAdminStats, 
  HotelStaffMember, 
  HotelSettings, 
  RecentAdminActivity,
  HotelRevenueData
} from '../types/admin';
import { API_BASE_URL } from '../config/apiConfig';

class HotelAdminService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  async getHotelStats(): Promise<HotelAdminStats> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${API_BASE_URL}/hotel/statistics/admin`, {
        headers: this.getAuthHeaders(),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error('Impossible de charger les statistiques');
      }
      const data = await response.json();
      // Map snake_case backend response to camelCase frontend type
      return {
        totalRooms: data.total_rooms || 0,
        occupiedRooms: data.occupied_rooms || 0,
        occupancyRate: data.occupancy_rate || 0,
        totalGuests: data.active_guests || 0,
        checkInsToday: data.active_guests || 0,
        checkOutsToday: data.completed_stays || 0,
        pendingCheckIns: 0,
        totalRevenue: data.revenue_ttc || 0,
        averageStayDuration: 0,
        staffCount: 0,
        recentRegistrations: data.active_guests || 0,
      };
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }
  }

  /** Generate a cryptographically random temporary password */
  private generateSecurePassword(): string {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const digits = '23456789';
    const special = '!@#$%&*';
    const all = upper + lower + digits + special;
    const arr = new Uint8Array(14);
    crypto.getRandomValues(arr);
    let pw = '';
    pw += upper[arr[0] % upper.length];
    pw += lower[arr[1] % lower.length];
    pw += digits[arr[2] % digits.length];
    pw += special[arr[3] % special.length];
    for (let i = 4; i < 14; i++) {
      pw += all[arr[i] % all.length];
    }
    return pw;
  }

  async getStaffMembers(): Promise<HotelStaffMember[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(`${API_BASE_URL}/hotel/users`, {
        headers: this.getAuthHeaders(),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error('Failed to fetch staff members');
      }
      const data = await response.json();
      // API returns {users: [...], count} wrapper
      const users = Array.isArray(data) ? data : (data.users || []);
      // Map backend response to frontend type
      return users.map((user: any) => ({
        id: user.id,
        fullName: user.full_name,
        email: user.email,
        phone: user.phone || '',
        role: user.role,
        status: user.is_active ? 'active' : 'inactive',
        lastLogin: user.last_login_at || undefined,
        createdAt: user.created_at,
      }));
    } catch (error) {
      console.error('Failed to fetch staff members:', error);
      return [];
    }
  }

  async createStaffMember(staffData: Omit<HotelStaffMember, 'id' | 'createdAt' | 'lastLogin'> & { password?: string }): Promise<HotelStaffMember> {
    try {
      const password = staffData.password || this.generateSecurePassword();
      const response = await fetch(`${API_BASE_URL}/hotel/users`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          email: staffData.email,
          full_name: staffData.fullName,
          phone: staffData.phone,
          role: staffData.role,
          password,
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to create staff member');
      }

      const user = await response.json();
      return {
        id: user.id,
        fullName: user.full_name,
        email: user.email,
        phone: user.phone || '',
        role: user.role,
        status: user.is_active ? 'active' : 'inactive',
        lastLogin: user.last_login_at || undefined,
        createdAt: user.created_at,
      };
    } catch (error) {
      console.error('Failed to create staff member:', error);
      throw error;
    }
  }

  async updateStaffMember(id: string, staffData: Partial<HotelStaffMember>): Promise<HotelStaffMember> {
    try {
      const body: any = {};
      if (staffData.fullName) body.full_name = staffData.fullName;
      if (staffData.phone) body.phone = staffData.phone;
      if (staffData.role) body.role = staffData.role;
      if (staffData.status !== undefined) body.is_active = staffData.status === 'active';

      const response = await fetch(`${API_BASE_URL}/hotel/users/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to update staff member');
      }
      
      const user = await response.json();
      return {
        id: user.id,
        fullName: user.full_name,
        email: user.email,
        phone: user.phone || '',
        role: user.role,
        status: user.is_active ? 'active' : 'inactive',
        lastLogin: user.last_login_at || undefined,
        createdAt: user.created_at,
      };
    } catch (error) {
      console.error('Failed to update staff member:', error);
      throw error;
    }
  }

  async deleteStaffMember(id: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/hotel/users/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to delete staff member');
      }
    } catch (error) {
      console.error('Failed to delete staff member:', error);
      throw error;
    }
  }

  async resetStaffPassword(id: string): Promise<{ temporaryPassword: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/hotel/users/${id}/reset-password`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to reset password');
      }
      
      const data = await response.json();
      return {
        temporaryPassword: data.temporary_password
      };
    } catch (error) {
      console.error('Failed to reset password:', error);
      throw error;
    }
  }

  async getHotelSettings(): Promise<HotelSettings> {
    try {
      const response = await fetch(`${API_BASE_URL}/hotel/settings`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch hotel settings');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch hotel settings:', error);
      throw error;
    }
  }

  async updateHotelSettings(settings: HotelSettings): Promise<HotelSettings> {
    try {
      const response = await fetch(`${API_BASE_URL}/hotel/settings`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(settings)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update hotel settings');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to update hotel settings:', error);
      throw error;
    }
  }

  async getRecentActivity(): Promise<RecentAdminActivity[]> {
    try {
      // Use hotel-scoped statistics endpoint instead of platform admin audit-logs
      // (hotel_admin role does not have access to /admin/audit-logs)
      const response = await fetch(`${API_BASE_URL}/hotel/settings/history`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        return [];
      }
      const logs = await response.json();
      return (Array.isArray(logs) ? logs : []).map((log: any) => ({
        id: log.id,
        type: log.action || log.change_type || 'unknown',
        description: `${log.action || log.change_type || ''} - ${log.resource_type || log.setting_key || ''}`.trim(),
        performedBy: log.user_role || log.changed_by || 'system',
        timestamp: log.created_at || log.changed_at,
      }));
    } catch {
      return [];
    }
  }

  async getRevenueData(days: number = 7): Promise<HotelRevenueData[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/hotel/statistics/admin`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        return [];
      }
      // Backend returns aggregated stats; build daily breakdown from available data
      const stats = await response.json();
      const data: HotelRevenueData[] = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        data.push({
          date: date.toISOString().split('T')[0],
          revenue: stats.total_revenue ? stats.total_revenue / days : 0,
          bookings: stats.checkins_today || 0,
        });
      }
      return data;
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
      return [];
    }
  }
}

export const hotelAdminService = new HotelAdminService();
